import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { BrandKit } from 'models';
import { handleSearchQuery } from 'utils/utils';

import { Status, Card, Search, Dropdown, AdvancedFilter, ContentCard, ToastContext, Table, TableCellDropdown, DropdownItem, StopPropagation } from '@weezevent/nacre';

import { LoadingTable, PaginationDropDown, Paginator, SauronEmptyCard } from 'components';

import css from '../templates.module.css';

const spec = {
    multiple: [],
    fields: ['name', 'enabled', 'search']
};

export const BrandKitsList = ({ organizationId }) => {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [filters, setFilters] = React.useState({});
    const [pageSize, setPageSize] = React.useState(() => 50);
    const [currentPage, setCurrentPage] = React.useState(() => 0);

    const [brandkits, loadingBrandkits, syncBrandKits] = BrandKit.useApiModel(
        {
            organizationId,
            cache: false,
            allow_cache: false,
            query: {
                limit: pageSize,
                offset: currentPage * pageSize,
                ...handleSearchQuery(spec, filters, true)
            },
            onError: () => {
                toast.error(t('sauron.toasts.error'));
            }
        },
        [organizationId, filters, pageSize, currentPage]
    );

    const totalCount = React.useMemo(() => {
        if (!brandkits || loadingBrandkits) {
            return;
        }
        return +brandkits?.__meta?.headers?.get('total-count');
    }, [brandkits, loadingBrandkits]);

    const handleSearch = React.useCallback(
        value => {
            let _filters = { ...filters, search: value };
            if (!value) delete _filters['search'];
            setFilters(_filters);
        },
        [filters]
    );

    const handleAction = React.useCallback(
        (brandKit, action) => {
            brandKit[action]()
                .then(response => {
                    if (response.status === 200) {
                        syncBrandKits();
                    }
                })
                .catch(() => toast.error(t('sauron.toasts.error')));
        },
        [syncBrandKits]
    );

    const handleDelete = React.useCallback(
        brandKit => {
            brandKit
                .delete({ organizationId, id: brandKit.pk })
                .then(response => {
                    if (response.status === 204) {
                        syncBrandKits();
                    }
                })
                .catch(() => toast.error(t('sauron.toasts.error')));
        },
        [syncBrandKits, organizationId]
    );

    return (
        <Card>
            <ContentCard>
                <AdvancedFilter
                    value={filters}
                    onChange={values => setFilters(values ?? {})}
                    total={{
                        count: totalCount || 0,
                        loading: loadingBrandkits,
                        label: t('sauron.emails.templates.brandkits.countTotal', { count: totalCount || 0 })
                    }}
                    filters={[
                        {
                            name: 'enabled',
                            labelName: t('sauron.emails.templates.brandkits.filters.status'),
                            multiple: false,
                            options: [
                                { key: '0', text: 'enabled', value: true, name: true },
                                { key: '1', text: 'disabled', value: false, name: false }
                            ]
                        }
                    ]}
                    translations={{
                        getApplyFilters: () => t('common.cta.apply'),
                        labelFilters: t('common.filters'),
                        labelReset: t('common.cta.reset'),
                        labelFilterBy: t('common.cta.filter_by'),
                        labelCancel: t('common.cta.cancel')
                    }}
                    leftComponent={
                        <Search
                            icon="search"
                            value={filters?.search}
                            placeholder={t('common.search')}
                            onChange={(_, value) => handleSearch(value)}
                            handleReset={() => setFilters(filters => ({ ...filters, search: '' }))}
                        />
                    }
                />
                <PaginationDropDown
                    label={'sauron.emails.templates.brandkits.page_size'}
                    displaySelect={false}
                    count={totalCount}
                    limit={pageSize}
                    pageSizes={[50, 100]}
                    handlePage={setCurrentPage}
                    onChange={value => {
                        setPageSize(value);
                        setCurrentPage(0);
                    }}
                />
            </ContentCard>
            <Table useNew>
                <Table.Header>
                    <Table.Tr>
                        <th>{t(`common.name`)}</th>
                        <th className={css['width-small']}>{t(`sauron.emails.templates.brandkits.templates_count`)}</th>
                        <th className={css['width-medium']}>{t(`common.status`)}</th>
                        <th className={css['width-small']}>{t(`common.actions`)}</th>
                    </Table.Tr>
                </Table.Header>
                {!!brandkits?.length && !loadingBrandkits && (
                    <Table.TBody>
                        {brandkits?.map(kit => (
                            <BrandKitRow key={kit.id} kit={kit} organizationId={organizationId} handleAction={handleAction} handleDelete={handleDelete} />
                        ))}
                    </Table.TBody>
                )}
            </Table>
            <LoadingTable loading={loadingBrandkits} height="30vh" />
            {!loadingBrandkits &&
                (brandkits?.length > 0 ? (
                    <Paginator currentPage={currentPage} onPageChange={setCurrentPage} pageSize={pageSize} totalCount={totalCount} />
                ) : (
                    <SauronEmptyCard title={t('sauron.emails.templates.brandkits.empty-card-title')} subTitle={t('sauron.emails.templates.brandkits.empty-card-subtitle')} />
                ))}
        </Card>
    );
};

const BrandKitRow = ({ kit, handleAction, handleDelete }) => {
    const { t } = useTranslation();
    const history = useHistory();

    return (
        <Table.Tr onClick={() => history.push(`brandkits/${kit.id}`)}>
            <Table.Td>{kit.name}</Table.Td>
            <Table.Td>{kit.templates_count}</Table.Td>
            <Table.Td>
                <StopPropagation>
                    <Status.Dropdown
                        color={kit.status.color}
                        items={[
                            <DropdownItem key="0" disabled={kit.enabled} onClick={() => handleAction(kit, 'enable')} item={t('common.cta.enable')} />,
                            <DropdownItem key="1" disabled={!kit.enabled} onClick={() => handleAction(kit, 'disable')} item={t('common.cta.disable')} />
                        ]}
                        text={t(`common.${kit.status.text}`)}
                    />
                </StopPropagation>
            </Table.Td>
            <TableCellDropdown>
                <Dropdown.Item item={t('common.cta.delete')} deleteItem onClick={() => handleDelete(kit)} />
            </TableCellDropdown>
        </Table.Tr>
    );
};
